import { useState } from "react";
import CopyButton from "./CopyButton";
import stealthnetLogo from "../assets/stealthnet_ninja_transparent_logo.png";
import {
  buildCoverPageDocument,
  buildConfidentialityNoticeDocument,
  buildFindingDocument,
  buildReportOutline,
  buildProjectDetailsDocument,
  getRichTextBlocks,
} from "../utils/formatFindingHtml";

const FINDING_SECTIONS = [
  {
    field: "final_definition",
    anchorSuffix: "definition",
    generatedField: "generated_definition",
    title: "Definition",
    emptyText: "Click here to draft the definition for this finding.",
    minRows: 6,
  },
  {
    field: "final_details",
    anchorSuffix: "details",
    generatedField: "generated_details",
    title: "Details",
    emptyText: "Click here to draft the detailed report narrative.",
    minRows: 14,
  },
  {
    field: "location",
    anchorSuffix: "location",
    generatedField: "",
    title: "Location",
    emptyText: "Click here to add URLs, hosts, paths, or endpoints.",
    minRows: 4,
  },
  {
    field: "final_remediation",
    anchorSuffix: "remediation",
    generatedField: "generated_remediation",
    title: "Remediation",
    emptyText: "Click here to draft remediation guidance.",
    minRows: 10,
  },
];

const REPORT_SECTIONS = [
  {
    field: "executive_summary",
    title: "Executive Summary",
    anchorId: "section-executive-summary",
    emptyText: "Click here to draft or refine the executive summary.",
    minRows: 16,
  },
  {
    field: "methodology_text",
    title: "Methodology",
    anchorId: "section-methodology",
    emptyText: "Click here to draft or refine the methodology.",
    minRows: 16,
  },
];

const SEVERITY_OPTIONS = ["Critical", "High", "Medium", "Low", "Informational"];

const PROJECT_DETAILS_TEXT_SECTIONS = [
  {
    field: "project_details_overview",
    anchorId: "section-project-details-overview",
    title: "Overview",
    emptyText: "Click here to add the project overview for this engagement.",
    minRows: 8,
  },
  {
    field: "scope_summary",
    anchorId: "section-project-details-scope",
    title: "Scope",
    emptyText: "Click here to add the assessed scope, hosts, domains, or environments.",
    minRows: 8,
  },
];

const CONFIDENTIALITY_NOTICE_SECTION = {
  field: "confidentiality_notice",
  title: "Confidentiality Notice",
  emptyText: "Click here to add the confidentiality notice for this engagement.",
  minRows: 12,
};

function renderTextBlock(value, emptyText) {
  const blocks = getRichTextBlocks(value);
  if (!blocks.length) {
    return <p className="doc-empty-copy">{emptyText}</p>;
  }

  return blocks.map((block, index) => {
    if (block.type === "ul") {
      return (
        <ul key={`ul-${index}`}>
          {block.items.map((item, itemIndex) => (
            <li key={`${item}-${itemIndex}`}>{item}</li>
          ))}
        </ul>
      );
    }

    if (block.type === "ol") {
      return (
        <ol key={`ol-${index}`}>
          {block.items.map((item, itemIndex) => (
            <li key={`${item}-${itemIndex}`}>{item}</li>
          ))}
        </ol>
      );
    }

    return (
      <p key={`p-${index}`}>
        {block.lines.map((line, lineIndex) => (
          <span key={`${line}-${lineIndex}`}>
            {line}
            {lineIndex < block.lines.length - 1 ? <br /> : null}
          </span>
        ))}
      </p>
    );
  });
}

function getFindingSectionValue(finding, field, generatedField) {
  if (field === "location") {
    return finding.location || "";
  }

  return finding[field] || finding[generatedField] || "";
}

function getTextareaRows(value, minRows) {
  const lineCount = String(value || "").split("\n").length;
  return Math.max(minRows, lineCount + 1);
}

function hasOwn(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

function parseCountOverride(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return null;
  }

  const parsed = Number.parseInt(trimmed, 10);
  if (Number.isNaN(parsed) || parsed < 0) {
    return 0;
  }

  return parsed;
}

export default function ReportDocument({
  report,
  onReportFieldChange,
  onSaveReport,
  onGenerateExecutiveSummary,
  onGenerateMethodology,
  onCopyAll,
  onDownloadHtml,
  onDownloadWord,
  onCopyExecutiveSummary,
  onCopyMethodology,
  isSavingReport,
  isGeneratingExecutiveSummary,
  isGeneratingMethodology,
  onCreateNewFinding,
  onOpenFindingDetails,
  onDeleteFinding,
  onMoveFinding,
  onFindingFieldChange,
  onSaveFinding,
  onRequestGenerateFinding,
  onCopyFinding,
  isSavingFinding,
  isReorderingFindings,
  findingGenerationState,
}) {
  const [editingKey, setEditingKey] = useState("");
  const [snapshots, setSnapshots] = useState({});
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [tableDrafts, setTableDrafts] = useState({});

  const confidentialityNotice = buildConfidentialityNoticeDocument(report);
  const coverPage = buildCoverPageDocument(report);
  const projectDetails = buildProjectDetailsDocument(report);
  const reportOutline = buildReportOutline(report);

  function startEditing(key, value, prime) {
    if (prime) {
      prime();
    }
    setSnapshots((currentSnapshots) => ({ ...currentSnapshots, [key]: value || "" }));
    setEditingKey(key);
  }

  function cancelEditing(key, restore) {
    if (Object.prototype.hasOwnProperty.call(snapshots, key)) {
      restore(snapshots[key]);
    }
    setEditingKey("");
  }

  async function finishEditing(key, currentValue, saveHandler) {
    if (editingKey !== key) {
      return;
    }

    const snapshotValue = snapshots[key] ?? "";
    if ((currentValue || "") === snapshotValue) {
      setEditingKey("");
      return;
    }

    setIsAutoSaving(true);
    const saved = await saveHandler();
    setIsAutoSaving(false);

    if (saved) {
      setEditingKey("");
    }
  }

  async function handleSeveritySave(findingId, severity) {
    const key = `finding:${findingId}:severity`;
    onFindingFieldChange(findingId, "severity", severity);
    setIsAutoSaving(true);
    const saved = await onSaveFinding(findingId, { silent: true });
    setIsAutoSaving(false);

    if (saved) {
      setEditingKey("");
      setSnapshots((currentSnapshots) => ({ ...currentSnapshots, [key]: severity }));
    }
  }

  function getTableDraftValue(key, fallbackValue) {
    return hasOwn(tableDrafts, key) ? tableDrafts[key] : fallbackValue;
  }

  function handleTableDraftChange(key, value) {
    setTableDrafts((currentDrafts) => ({ ...currentDrafts, [key]: value }));
  }

  async function handleProjectFieldSave(field, value, draftKey) {
    const nextReport = { ...report, [field]: value };
    onReportFieldChange(field, value);
    setIsAutoSaving(true);
    const saved = await onSaveReport({ silent: true, reportOverride: nextReport });
    setIsAutoSaving(false);

    if (saved && draftKey) {
      setTableDrafts((currentDrafts) => {
        const nextDrafts = { ...currentDrafts };
        delete nextDrafts[draftKey];
        return nextDrafts;
      });
    }
  }

  return (
    <section className="report-document-layout">
      <section className="panel report-document-toolbar-panel">
        <div className="report-document-toolbar-copy">
          <p className="eyebrow">Google Docs Preview</p>
          <h2>{report.report_title || "Untitled Report"}</h2>
          <p className="doc-toolbar-note">
            Click directly into the document to edit. Changes save when you click outside a section.
            {isAutoSaving || isSavingReport || isSavingFinding ? " Saving..." : ""}
          </p>
        </div>
        <div className="toolbar-row">
          <button type="button" className="ghost-button" onClick={onCreateNewFinding}>
            New Finding
          </button>
          <button type="button" className="ghost-button" onClick={onDownloadHtml}>
            Download HTML
          </button>
          <button type="button" className="ghost-button" onClick={onDownloadWord}>
            Download Word
          </button>
          <CopyButton label="Copy All" getPayload={onCopyAll} className="primary-button" />
        </div>
      </section>

      <div className="document-canvas report-document-canvas">
        <div className="report-document-stack">
          <article className="doc-page report-master-doc report-cover-page">
            <section id="section-cover-page" data-report-anchor className="report-cover-stage">
              <div className="report-cover-backdrop report-cover-orb report-cover-orb-primary" aria-hidden="true" />
              <div className="report-cover-backdrop report-cover-orb report-cover-orb-secondary" aria-hidden="true" />
              <div className="report-cover-backdrop report-cover-grid" aria-hidden="true" />
              <div className="report-cover-logo-wrap" aria-hidden="true">
                <img src={stealthnetLogo} alt="" className="report-cover-logo" />
              </div>

              {editingKey === "report:report_delivery_date" ? (
                <input
                  type="date"
                  className="cover-edit-input cover-date-input"
                  autoFocus
                  value={report.report_delivery_date || ""}
                  onChange={(event) => onReportFieldChange("report_delivery_date", event.target.value)}
                  onBlur={() =>
                    finishEditing("report:report_delivery_date", report.report_delivery_date || "", () =>
                      onSaveReport({ silent: true }),
                    )
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      event.preventDefault();
                      cancelEditing("report:report_delivery_date", (snapshot) =>
                        onReportFieldChange("report_delivery_date", snapshot),
                      );
                    }
                    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                      event.preventDefault();
                      finishEditing("report:report_delivery_date", report.report_delivery_date || "", () =>
                        onSaveReport({ silent: true }),
                      );
                    }
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="cover-edit-button cover-date-button"
                  onClick={() => startEditing("report:report_delivery_date", report.report_delivery_date || "")}
                >
                  {coverPage.dateDisplay}
                </button>
              )}

              <div className="report-cover-center">
                <p className="report-cover-kicker">{report.report_title || "StealthNet AI Security Report"}</p>
                <h1 className="report-cover-title">{coverPage.title}</h1>
                {editingKey === "report:assessment_type" ? (
                  <input
                    className="cover-edit-input cover-subtitle-input"
                    autoFocus
                    value={report.assessment_type || ""}
                    onChange={(event) => onReportFieldChange("assessment_type", event.target.value)}
                    onBlur={() =>
                      finishEditing("report:assessment_type", report.assessment_type || "", () =>
                        onSaveReport({ silent: true }),
                      )
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        event.preventDefault();
                        cancelEditing("report:assessment_type", (snapshot) =>
                          onReportFieldChange("assessment_type", snapshot),
                        );
                      }
                      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                        event.preventDefault();
                        finishEditing("report:assessment_type", report.assessment_type || "", () =>
                          onSaveReport({ silent: true }),
                        );
                      }
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    className="cover-edit-button report-cover-subtitle"
                    onClick={() => startEditing("report:assessment_type", report.assessment_type || "")}
                  >
                    {coverPage.subtitle}
                  </button>
                )}
              </div>

              <footer className="report-cover-footer">
                <div className="report-cover-brand-block">
                  <div>
                    <p className="report-cover-footer-label">Prepared By</p>
                    <p className="report-cover-footer-value">{coverPage.brandName}</p>
                  </div>
                </div>

                <div className="report-cover-client-block">
                  <p className="report-cover-footer-label">Prepared For</p>
                  {editingKey === "report:client_name" ? (
                    <input
                      className="cover-edit-input cover-client-input"
                      autoFocus
                      value={report.client_name || ""}
                      onChange={(event) => onReportFieldChange("client_name", event.target.value)}
                      onBlur={() =>
                        finishEditing("report:client_name", report.client_name || "", () =>
                          onSaveReport({ silent: true }),
                        )
                      }
                      onKeyDown={(event) => {
                        if (event.key === "Escape") {
                          event.preventDefault();
                          cancelEditing("report:client_name", (snapshot) => onReportFieldChange("client_name", snapshot));
                        }
                        if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                          event.preventDefault();
                          finishEditing("report:client_name", report.client_name || "", () =>
                            onSaveReport({ silent: true }),
                          );
                        }
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      className="cover-edit-button report-cover-client"
                      onClick={() => startEditing("report:client_name", report.client_name || "")}
                    >
                      {coverPage.clientName}
                    </button>
                  )}
                </div>
              </footer>
            </section>
          </article>

          <article className="doc-page report-master-doc report-content-page">
            <section id="section-table-of-contents" data-report-anchor className="doc-section report-major-section report-page-section">
              <div className="doc-section-header report-section-actions">
                <h2>Table of Contents</h2>
              </div>

              <div className="report-doc-table-of-contents" aria-label="Report table of contents page">
                {reportOutline.tocEntries.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    className={`report-doc-toc-row level-${entry.level}`}
                    onClick={() => {
                      document.getElementById(entry.id)?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }}
                  >
                    <span className="report-doc-toc-label">{entry.label}</span>
                    <span className="report-doc-toc-dots" aria-hidden="true" />
                    <span className="report-doc-toc-page">{entry.pageNumber}</span>
                  </button>
                ))}
              </div>
            </section>
          </article>

          <article className="doc-page report-master-doc report-content-page">
            <section
              id="section-confidentiality-notice"
              data-report-anchor
              className="doc-section report-major-section report-page-section"
            >
              <div className="doc-section-header report-section-actions">
                <h2>{CONFIDENTIALITY_NOTICE_SECTION.title}</h2>
              </div>

              {editingKey === `report:${CONFIDENTIALITY_NOTICE_SECTION.field}` ? (
                <textarea
                  className="doc-editor"
                  autoFocus
                  rows={getTextareaRows(
                    report[CONFIDENTIALITY_NOTICE_SECTION.field] || confidentialityNotice.body,
                    CONFIDENTIALITY_NOTICE_SECTION.minRows,
                  )}
                  value={report[CONFIDENTIALITY_NOTICE_SECTION.field] || confidentialityNotice.body}
                  onChange={(event) => onReportFieldChange(CONFIDENTIALITY_NOTICE_SECTION.field, event.target.value)}
                  onBlur={() =>
                    finishEditing(
                      `report:${CONFIDENTIALITY_NOTICE_SECTION.field}`,
                      report[CONFIDENTIALITY_NOTICE_SECTION.field] || confidentialityNotice.body,
                      () => onSaveReport({ silent: true }),
                    )
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      event.preventDefault();
                      cancelEditing(`report:${CONFIDENTIALITY_NOTICE_SECTION.field}`, (snapshot) =>
                        onReportFieldChange(CONFIDENTIALITY_NOTICE_SECTION.field, snapshot),
                      );
                    }
                    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                      event.preventDefault();
                      finishEditing(
                        `report:${CONFIDENTIALITY_NOTICE_SECTION.field}`,
                        report[CONFIDENTIALITY_NOTICE_SECTION.field] || confidentialityNotice.body,
                        () => onSaveReport({ silent: true }),
                      );
                    }
                  }}
                />
              ) : (
                <div
                  className="doc-edit-surface confidentiality-notice-surface"
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    startEditing(
                      `report:${CONFIDENTIALITY_NOTICE_SECTION.field}`,
                      report[CONFIDENTIALITY_NOTICE_SECTION.field] || confidentialityNotice.body,
                    )
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      startEditing(
                        `report:${CONFIDENTIALITY_NOTICE_SECTION.field}`,
                        report[CONFIDENTIALITY_NOTICE_SECTION.field] || confidentialityNotice.body,
                      );
                    }
                  }}
                >
                  {renderTextBlock(
                    report[CONFIDENTIALITY_NOTICE_SECTION.field] || confidentialityNotice.body,
                    CONFIDENTIALITY_NOTICE_SECTION.emptyText,
                  )}
                </div>
              )}
            </section>
          </article>

          <article className="doc-page report-master-doc report-content-page">
            <section
              id="section-project-details"
              data-report-anchor
              className="doc-section report-major-section report-page-section"
            >
              <div className="doc-section-header report-section-actions">
                <h2>Project Details</h2>
              </div>

              {PROJECT_DETAILS_TEXT_SECTIONS.slice(0, 1).map((section) => {
                const key = `report:${section.field}`;
                const value = report[section.field] || projectDetails.overview;
                const isEditing = editingKey === key;

                return (
                  <section
                    key={section.field}
                    id={section.anchorId}
                    data-report-anchor
                    className="doc-subsection project-details-subsection"
                  >
                    <div className="doc-section-header">
                      <h3>{section.title}</h3>
                    </div>

                    {isEditing ? (
                      <textarea
                        className="doc-editor"
                        autoFocus
                        rows={getTextareaRows(value, section.minRows)}
                        value={value}
                        onChange={(event) => onReportFieldChange(section.field, event.target.value)}
                        onBlur={() => finishEditing(key, value, () => onSaveReport({ silent: true }))}
                        onKeyDown={(event) => {
                          if (event.key === "Escape") {
                            event.preventDefault();
                            cancelEditing(key, (snapshot) => onReportFieldChange(section.field, snapshot));
                          }
                          if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                            event.preventDefault();
                            finishEditing(key, value, () => onSaveReport({ silent: true }));
                          }
                        }}
                      />
                    ) : (
                      <div
                        className="doc-edit-surface project-details-intro"
                        role="button"
                        tabIndex={0}
                        onClick={() => startEditing(key, value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            startEditing(key, value);
                          }
                        }}
                      >
                        {renderTextBlock(value, section.emptyText)}
                      </div>
                    )}
                  </section>
                );
              })}

              <section
                id="section-project-details-dates"
                data-report-anchor
                className="doc-subsection project-details-subsection"
              >
                <div className="doc-section-header">
                  <h3>Dates</h3>
                </div>
                <div className="project-details-table-wrap">
                  <table className="project-details-table">
                    <thead>
                      <tr>
                        {projectDetails.dates.map((item) => (
                          <th key={item.key} scope="col">
                            {item.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {projectDetails.dates.map((item) => {
                          const draftKey = `project-date:${item.key}`;
                          return (
                            <td key={item.key}>
                              <input
                                type="date"
                                className="doc-table-input"
                                value={getTableDraftValue(draftKey, report[item.key] || "")}
                                onChange={(event) => handleTableDraftChange(draftKey, event.target.value)}
                                onBlur={(event) => handleProjectFieldSave(item.key, event.target.value, draftKey)}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section
                id="section-project-details-findings"
                data-report-anchor
                className="doc-subsection project-details-subsection"
              >
                <div className="doc-section-header">
                  <h3>Findings</h3>
                </div>
                <div className="project-details-table-wrap">
                  <table className="project-details-table project-findings-table">
                    <thead>
                      <tr>
                        {projectDetails.findings.map((item) => (
                          <th key={item.key} scope="col">
                            <span className={`severity-pill severity-${item.label.toLowerCase()}`}>{item.label}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {projectDetails.findings.map((item) => {
                          const draftKey = `project-count:${item.key}`;
                          return (
                            <td key={item.key}>
                              <input
                                type="text"
                                inputMode="numeric"
                                className="doc-table-input doc-table-input-centered"
                                value={getTableDraftValue(draftKey, String(item.value))}
                                onChange={(event) => {
                                  const nextValue = event.target.value.replace(/[^\d]/g, "");
                                  handleTableDraftChange(draftKey, nextValue);
                                }}
                                onBlur={(event) =>
                                  handleProjectFieldSave(item.key, parseCountOverride(event.target.value), draftKey)
                                }
                              />
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {PROJECT_DETAILS_TEXT_SECTIONS.slice(1).map((section) => {
                const key = `report:${section.field}`;
                const value = report[section.field] || "";
                const isEditing = editingKey === key;

                return (
                  <section
                    key={section.field}
                    id={section.anchorId}
                    data-report-anchor
                    className="doc-subsection project-details-subsection"
                  >
                    <div className="doc-section-header">
                      <h3>{section.title}</h3>
                    </div>

                    {isEditing ? (
                      <textarea
                        className="doc-editor"
                        autoFocus
                        rows={getTextareaRows(value, section.minRows)}
                        value={value}
                        onChange={(event) => onReportFieldChange(section.field, event.target.value)}
                        onBlur={() => finishEditing(key, value, () => onSaveReport({ silent: true }))}
                        onKeyDown={(event) => {
                          if (event.key === "Escape") {
                            event.preventDefault();
                            cancelEditing(key, (snapshot) => onReportFieldChange(section.field, snapshot));
                          }
                          if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                            event.preventDefault();
                            finishEditing(key, value, () => onSaveReport({ silent: true }));
                          }
                        }}
                      />
                    ) : (
                      <div
                        className="doc-edit-surface"
                        role="button"
                        tabIndex={0}
                        onClick={() => startEditing(key, value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            startEditing(key, value);
                          }
                        }}
                      >
                        {renderTextBlock(value, section.emptyText)}
                      </div>
                    )}
                  </section>
                );
              })}
            </section>
          </article>

          {REPORT_SECTIONS.map((section) => {
            const key = `report:${section.field}`;
            const isEditing = editingKey === key;
            const value = report[section.field] || "";
            const isGenerating =
              section.field === "executive_summary" ? isGeneratingExecutiveSummary : isGeneratingMethodology;
            const handleGenerate =
              section.field === "executive_summary" ? onGenerateExecutiveSummary : onGenerateMethodology;
            const handleCopy =
              section.field === "executive_summary" ? onCopyExecutiveSummary : onCopyMethodology;

            return (
              <article key={section.field} className="doc-page report-master-doc report-content-page">
                <section id={section.anchorId} data-report-anchor className="doc-section report-major-section report-page-section">
                  <div className="doc-section-header report-section-actions">
                    <h2>{section.title}</h2>
                    <div className="toolbar-row">
                      <CopyButton label="Copy" disabled={!value} getPayload={handleCopy} className="ghost-button" />
                      <button type="button" className="ghost-button" onClick={handleGenerate} disabled={isGenerating}>
                        {isGenerating ? "Generating..." : value ? "Refine" : "Generate"}
                      </button>
                    </div>
                  </div>

                  {isEditing ? (
                    <textarea
                      className="doc-editor"
                      autoFocus
                      rows={getTextareaRows(value, section.minRows)}
                      value={value}
                      onChange={(event) => onReportFieldChange(section.field, event.target.value)}
                      onBlur={() => finishEditing(key, value, () => onSaveReport({ silent: true }))}
                      onKeyDown={(event) => {
                        if (event.key === "Escape") {
                          event.preventDefault();
                          cancelEditing(key, (snapshot) => onReportFieldChange(section.field, snapshot));
                        }
                        if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                          event.preventDefault();
                          finishEditing(key, value, () => onSaveReport({ silent: true }));
                        }
                      }}
                    />
                  ) : (
                    <div
                      className="doc-edit-surface"
                      role="button"
                      tabIndex={0}
                      onClick={() => startEditing(key, value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          startEditing(key, value);
                        }
                      }}
                    >
                      {renderTextBlock(value, section.emptyText)}
                    </div>
                  )}
                </section>
              </article>
            );
          })}

          <div id="section-findings" data-report-anchor className="report-anchor-marker" />

          {report.findings.length ? (
            report.findings.map((finding, index) => {
              const document = buildFindingDocument(finding);
              const titleKey = `finding:${finding.id}:title`;
              const severityKey = `finding:${finding.id}:severity`;
              const titleValue = finding.name || "";
              const isEditingTitle = editingKey === titleKey;
              const isEditingSeverity = editingKey === severityKey;
              const canGenerate = Boolean(finding.name && finding.severity && finding.location);
              const isGenerating =
                findingGenerationState.findingId === finding.id && findingGenerationState.target === "full";

              return (
                <article key={finding.id} className="doc-page report-master-doc report-content-page">
                  <section
                    id={`finding-${finding.id}`}
                    data-report-anchor
                    className="doc-section finding-doc-block report-page-section"
                  >
                    <div className="doc-section-header finding-block-header">
                      <div className="finding-block-heading">
                        <span className="finding-index-label">Finding {index + 1}</span>
                        {isEditingTitle ? (
                          <input
                            className="doc-title-input"
                            autoFocus
                            value={titleValue}
                            onChange={(event) => onFindingFieldChange(finding.id, "name", event.target.value)}
                            onBlur={() =>
                              finishEditing(titleKey, titleValue, () => onSaveFinding(finding.id, { silent: true }))
                            }
                            onKeyDown={(event) => {
                              if (event.key === "Escape") {
                                event.preventDefault();
                                cancelEditing(titleKey, (snapshot) => onFindingFieldChange(finding.id, "name", snapshot));
                              }
                              if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                                event.preventDefault();
                                finishEditing(titleKey, titleValue, () => onSaveFinding(finding.id, { silent: true }));
                              }
                            }}
                          />
                        ) : (
                          <button
                            type="button"
                            className="doc-title-button"
                            onClick={() => startEditing(titleKey, titleValue)}
                          >
                            {document.title}
                          </button>
                        )}
                      </div>

                      <div className="finding-block-actions">
                        {report.findings.length > 1 ? (
                          <label className="finding-position-control">
                            <span>Position</span>
                            <select
                              className="doc-position-select"
                              value={index}
                              onChange={(event) => onMoveFinding(finding.id, Number(event.target.value))}
                              disabled={isReorderingFindings}
                            >
                              {report.findings.map((_, optionIndex) => (
                                <option key={`${finding.id}-position-${optionIndex + 1}`} value={optionIndex}>
                                  {optionIndex + 1}
                                </option>
                              ))}
                            </select>
                          </label>
                        ) : null}

                        {isEditingSeverity ? (
                          <select
                            className="doc-severity-select"
                            autoFocus
                            value={finding.severity || "Medium"}
                            onChange={(event) => handleSeveritySave(finding.id, event.target.value)}
                            onBlur={() => setEditingKey("")}
                          >
                            {SEVERITY_OPTIONS.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <button
                            type="button"
                            className="doc-finding-severity"
                            style={{ color: document.severity.color }}
                            onClick={() => startEditing(severityKey, finding.severity || "Medium")}
                            title="Edit severity"
                          >
                            {document.severity.label}
                          </button>
                        )}

                        <CopyButton
                          label="Copy"
                          disabled={!finding.name}
                          getPayload={() => onCopyFinding(finding.id)}
                          className="ghost-button"
                        />
                        <button
                          type="button"
                          className="ghost-button"
                          onClick={() => onRequestGenerateFinding(finding.id)}
                          disabled={!canGenerate || isGenerating}
                        >
                          {isGenerating ? "Generating..." : "Refine All"}
                        </button>
                        <button type="button" className="ghost-button" onClick={() => onOpenFindingDetails(finding.id)}>
                          Details
                        </button>
                        <button type="button" className="ghost-button danger-button" onClick={() => onDeleteFinding(finding.id)}>
                          X Delete
                        </button>
                      </div>
                    </div>

                    {FINDING_SECTIONS.map((section) => {
                      const fieldKey = `finding:${finding.id}:${section.field}`;
                      const isEditing = editingKey === fieldKey;
                      const value = getFindingSectionValue(finding, section.field, section.generatedField);

                      return (
                        <section
                          key={section.field}
                          id={`finding-${finding.id}-${section.anchorSuffix}`}
                          data-report-anchor
                          className="doc-subsection"
                        >
                          <div className="doc-section-header">
                            <h3>{section.title}</h3>
                          </div>

                          {isEditing ? (
                            <textarea
                              className="doc-editor"
                              autoFocus
                              rows={getTextareaRows(value, section.minRows)}
                              value={value}
                              onChange={(event) => onFindingFieldChange(finding.id, section.field, event.target.value)}
                              onBlur={() =>
                                finishEditing(fieldKey, value, () => onSaveFinding(finding.id, { silent: true }))
                              }
                              onKeyDown={(event) => {
                                if (event.key === "Escape") {
                                  event.preventDefault();
                                  cancelEditing(fieldKey, (snapshot) =>
                                    onFindingFieldChange(finding.id, section.field, snapshot),
                                  );
                                }
                                if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                                  event.preventDefault();
                                  finishEditing(fieldKey, value, () => onSaveFinding(finding.id, { silent: true }));
                                }
                              }}
                            />
                          ) : (
                            <div
                              className="doc-edit-surface"
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                if (section.field !== "location" && !finding[section.field] && value) {
                                  onFindingFieldChange(finding.id, section.field, value);
                                }
                                startEditing(fieldKey, value);
                              }}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  if (section.field !== "location" && !finding[section.field] && value) {
                                    onFindingFieldChange(finding.id, section.field, value);
                                  }
                                  startEditing(fieldKey, value);
                                }
                              }}
                            >
                              {renderTextBlock(value, section.emptyText)}
                            </div>
                          )}
                        </section>
                      );
                    })}
                  </section>
                </article>
              );
            })
          ) : (
            <article className="doc-page report-master-doc report-content-page">
              <section id="section-findings" data-report-anchor className="doc-section report-major-section report-page-section">
                <div className="doc-section-header report-section-actions">
                  <h2>Findings</h2>
                  <div className="toolbar-row">
                    <button type="button" className="ghost-button" onClick={onCreateNewFinding}>
                      New Finding
                    </button>
                  </div>
                </div>
                <div className="doc-edit-surface findings-empty-state">
                  <p className="doc-empty-copy">
                    No findings are in this report yet. Use the New Finding button to add one.
                  </p>
                </div>
              </section>
            </article>
          )}
        </div>
      </div>
    </section>
  );
}
